import { harmonateTokens as t } from './harmonateTokens';

export const harmonatePageSx = {
  background: t.pageBg,
  color: t.white,
  p: 3,
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  // Keep light surface panels readable when nested on the dark page chrome
  '& > .MuiPaper-root': { color: t.text },
};

export const harmonatePreviewBannerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  mb: 2,
  px: 2,
  py: 1,
  borderRadius: t.buttonRadius,
  background: t.fadeGradient,
  color: t.white,
};

export const harmonatePageTitleSx = {
  fontWeight: 700,
  fontSize: '1.25rem',
  color: t.white,
  mb: 0.5,
};

export const harmonateCardSx = {
  borderRadius: t.cardRadius,
  bgcolor: t.listBg,
  color: t.text,
  boxShadow: t.cardShadow,
  overflow: 'hidden',
};

export const harmonateProceedButtonSx = {
  bgcolor: t.green,
  color: t.white,
  borderRadius: t.buttonRadius,
  px: 2.5,
  boxShadow: t.buttonShadow,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  '&:hover': {
    bgcolor: t.greenHover,
    color: t.white,
    boxShadow: t.buttonShadow,
  },
  '&.Mui-disabled': {
    bgcolor: t.line,
    color: t.fadedText,
  },
};

export const harmonateBlueButtonSx = {
  bgcolor: t.blue,
  color: t.white,
  borderRadius: t.buttonRadius,
  px: 2.5,
  boxShadow: t.buttonShadow,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  '&:hover': {
    bgcolor: t.blueHover,
    color: t.white,
    boxShadow: t.buttonShadow,
  },
  '&.Mui-disabled': {
    bgcolor: t.line,
    color: t.fadedText,
  },
};

export const harmonateUploadButtonSx = {
  bgcolor: t.darkBlue,
  color: t.white,
  borderRadius: t.buttonRadius,
  px: 2.5,
  boxShadow: t.buttonShadow,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  '&:hover': {
    bgcolor: t.darkBlueHover,
    color: t.white,
    boxShadow: t.buttonShadow,
  },
  '&.Mui-disabled': {
    bgcolor: t.line,
    color: t.fadedText,
  },
};

export const harmonateUploadOutlinedSx = {
  borderColor: t.darkBlue,
  color: t.text,
  borderRadius: t.buttonRadius,
  px: 2.5,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  bgcolor: t.white,
  '&:hover': {
    borderColor: t.darkBlue,
    bgcolor: t.secondaryTab,
    color: t.text,
  },
  '&.Mui-disabled': {
    borderColor: t.line,
    color: t.text,
    opacity: 0.45,
  },
};

export const harmonateCancelButtonSx = {
  color: t.text,
  borderRadius: t.buttonRadius,
  px: 2.5,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  borderColor: t.line,
  '&:hover': {
    borderColor: t.text,
    bgcolor: 'rgba(0, 0, 0, 0.06)',
  },
};

export const harmonateTabsSx = {
  px: 1,
  minHeight: 52,
  bgcolor: t.listBg,
  borderBottom: `1px solid ${t.line}`,
  '& .MuiTab-root': {
    textTransform: 'none',
    minHeight: 48,
    fontSize: '0.875rem',
    minWidth: 'auto',
    px: 2,
    py: 1,
    mx: 0.5,
    my: 1,
    borderRadius: '14px',
    color: t.text,
    fontWeight: 400,
    transition: 'background-color 0.2s, color 0.2s',
    '&:hover': {
      bgcolor: 'rgba(2, 107, 29, 0.12)',
    },
  },
  '& .Mui-selected': {
    bgcolor: `${t.green} !important`,
    color: `${t.white} !important`,
    fontWeight: 700,
    boxShadow: t.buttonShadow,
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
};

/** Dark table / panel header bar (e.g. Labor Dashboard section titles) — Schedule-button green */
export const harmonateTableHeaderBarSx = {
  bgcolor: t.green,
  color: t.white,
};

export const harmonateDataGridSx = {
  border: 'none',
  bgcolor: t.white,
  '& .MuiDataGrid-main': {
    bgcolor: t.white,
  },
  '& .MuiDataGrid-virtualScroller': {
    bgcolor: t.white,
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    bgcolor: t.white,
  },
  '& .MuiDataGrid-overlayWrapper': {
    bgcolor: t.white,
  },
  '& .MuiDataGrid-overlay': {
    bgcolor: t.white,
  },
  '& .MuiDataGrid-filler': {
    bgcolor: t.white,
    border: 'none',
  },
  '& .MuiDataGrid-scrollbarFiller': {
    bgcolor: t.white,
    border: 'none',
  },
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: t.white,
    color: t.text,
    fontWeight: 700,
    fontSize: '0.625rem',
    borderBottom: `1px solid ${t.line}`,
  },
  '& .MuiDataGrid-row': {
    borderBottom: 'none',
    bgcolor: t.white,
  },
  '& .MuiDataGrid-row:hover': {
    bgcolor: t.secondaryTab,
  },
  '& .MuiDataGrid-cell': {
    fontSize: '0.75rem',
    color: t.text,
    bgcolor: 'inherit',
    borderBottom: `1px solid ${t.line}`,
  },
};

export const harmonateLinkSx = {
  color: t.text,
  fontWeight: 500,
  fontSize: '0.75rem',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: t.text,
  },
};

export const harmonateSectionHeaderSx = {
  px: 2.5,
  py: 1.5,
  borderBottom: `1px solid ${t.line}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  bgcolor: t.white,
};

export const harmonateDashboardPageSx = {
  p: 3,
  background: t.pageBg,
  color: t.white,
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  '& > .MuiPaper-root': { color: t.text },
};

export const harmonateKpiCardSx = {
  p: 2,
  height: '100%',
  borderRadius: t.cardRadius,
  bgcolor: t.white,
  color: t.text,
  boxShadow: t.cardShadow,
};

export const harmonateDashboardTitleSx = {
  fontWeight: 700,
  fontSize: '1.25rem',
  color: t.white,
  mb: 0.5,
};

export const harmonateLinkButtonSx = {
  textTransform: 'none',
  color: t.white,
  fontWeight: 500,
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.12)',
    color: t.white,
  },
};

/** Compact fields for dashboard/toolbars on dark page backgrounds */
export const harmonateToolbarFieldSx = {
  '& .MuiInputLabel-root': {
    color: t.white,
    fontSize: '0.75rem',
    '&.Mui-focused': { color: t.white },
    '&.MuiInputLabel-shrink': {
      bgcolor: 'transparent',
      color: t.white,
      px: 0.25,
    },
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: 'transparent',
    borderRadius: '8px !important',
    height: 40,
    alignItems: 'center',
    color: t.white,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.75) !important',
      borderWidth: '1px',
      borderRadius: '8px',
    },
    '&:hover fieldset': { borderColor: `${t.white} !important` },
    '&.Mui-focused fieldset': {
      borderColor: `${t.white} !important`,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    px: 1.25,
    fontSize: '0.875rem',
    color: `${t.white} !important`,
    WebkitTextFillColor: t.white,
    py: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    caretColor: t.white,
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.85) !important',
      WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
      opacity: '1 !important',
    },
    '&::-webkit-input-placeholder': {
      color: 'rgba(255, 255, 255, 0.85) !important',
      WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
      opacity: '1 !important',
    },
  },
  '& .MuiInputBase-input::placeholder, & input::placeholder': {
    color: 'rgba(255, 255, 255, 0.85) !important',
    WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
    opacity: '1 !important',
  },
  '& .MuiSelect-select': {
    py: '0 !important',
    minHeight: '0 !important',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    color: t.white,
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: 'translate(12px, -8px) scale(0.75)',
  },
  '& .MuiSelect-icon': { color: t.white, top: 'calc(50% - 12px)' },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: 'invert(1)',
    opacity: 0.85,
  },
};

export const harmonateToolbarDateFieldSx = {
  width: 148,
  ...harmonateToolbarFieldSx,
};

export const harmonateToolbarBuFieldSx = {
  minWidth: 240,
  width: 240,
  ...harmonateToolbarFieldSx,
};

/**
 * Oval (pill) fields on fadeGradient pages — white labels/text/borders.
 * Use for toolbars that sit directly on the page gradient (e.g. GL Mapping systems).
 */
export const harmonateGradientFormFieldsSx = {
  '& .MuiInputLabel-root': {
    color: t.white,
    fontSize: '0.875rem',
    '&.Mui-focused': { color: t.white },
    '&.MuiInputLabel-shrink': {
      bgcolor: 'transparent',
      color: t.white,
      px: 0.25,
    },
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: t.fieldRadius,
    color: t.white,
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.55)',
      borderWidth: '1px',
      borderRadius: t.fieldRadius,
    },
    '& fieldset legend': {
      width: '0 !important',
      maxWidth: '0 !important',
      padding: 0,
      visibility: 'hidden',
    },
    '&:hover fieldset': {
      borderColor: t.white,
    },
    '&.Mui-focused fieldset': {
      borderColor: t.white,
      borderWidth: '1px',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.55)',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
    },
  },
  '& .MuiInputBase-input, & .MuiSelect-select': {
    fontSize: '0.875rem',
    color: t.white,
  },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder': {
    color: 'rgba(255, 255, 255, 0.55)',
    opacity: 1,
  },
  '& .MuiSelect-icon, & .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: t.white,
  },
  '& .MuiSvgIcon-root': {
    color: t.white,
  },
};

export const harmonateToolbarButtonSx = {
  textTransform: 'none',
  borderRadius: t.buttonRadius,
  height: 40,
  px: 2.5,
  bgcolor: 'rgba(255, 255, 255, 0.14)',
  color: t.white,
  borderColor: 'rgba(255, 255, 255, 0.75)',
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  fontWeight: 500,
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.28)',
    borderColor: t.white,
    color: t.white,
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.45)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  '& .MuiCircularProgress-root': {
    color: 'inherit',
  },
};

/** Simple flex row for toolbar controls — no background container */
export const harmonateToolbarSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
};

/** Page-header icons on the gradient — always white for contrast */
export const harmonatePlainIconButtonSx = {
  color: t.white,
  '& .MuiSvgIcon-root': {
    color: 'inherit',
  },
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.12)',
    color: t.white,
    '& .MuiSvgIcon-root': {
      color: 'inherit',
    },
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.45)',
    opacity: 1,
    '& .MuiSvgIcon-root': {
      color: 'inherit',
    },
  },
};

export const harmonateGridActionIconSx = {
  color: t.text,
  '&:hover': {
    bgcolor: t.secondaryTab,
    color: t.text,
  },
};

export const harmonateDialogPaperProps = {
  sx: { borderRadius: t.cardRadius, color: t.text },
};

export const harmonateDialogTitleSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  bgcolor: t.darkBlue,
  color: t.white,
  fontWeight: 700,
  fontSize: '1.25rem',
};

/** Padding under dark-blue DialogTitle (MUI zeroes pt after title — restore space) */
export const harmonateDialogContentSx = {
  px: 3,
  pb: 2,
  pt: '24px !important',
};

/** Form dialogs — content padding + oval fields + Tenant type scale */
export const harmonateDialogFormContentSx = {
  ...harmonateDialogContentSx,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

export const harmonateTextFieldSx = {
  '& .MuiInputBase-input': { fontSize: '0.875rem' },
  '& .MuiSelect-select': { fontSize: '0.875rem' },
};

export const harmonateInputLabelProps = {
  sx: { fontSize: '0.875rem' },
};

export const harmonateDialogCloseIconSx = {
  color: t.white,
};

export const harmonateSectionTitleSx = {
  fontWeight: 700,
  color: t.text,
};

export const harmonateAbortButtonSx = {
  bgcolor: t.red,
  color: t.white,
  borderRadius: t.buttonRadius,
  px: 2.5,
  boxShadow: t.buttonShadow,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  '&:hover': {
    bgcolor: t.redHover,
    color: t.white,
    boxShadow: t.buttonShadow,
  },
};

export const harmonateAccentLinkSx = {
  color: t.text,
  fontWeight: 500,
  textTransform: 'none',
  '&:hover': {
    color: t.text,
    bgcolor: 'transparent',
    textDecoration: 'underline',
  },
};

export const harmonateSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: t.green,
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: t.green,
  },
};

/** Figma text fields — pill radius + Tenant-matched 0.875rem type */
export const harmonateFormFieldsSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: t.fieldRadius,
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: t.text,
      borderWidth: '1px',
      borderRadius: t.fieldRadius,
    },
    // Keep a continuous oval: MUI's legend notch cuts the pill border and looks incomplete.
    '& fieldset legend': {
      width: '0 !important',
      maxWidth: '0 !important',
      padding: 0,
      visibility: 'hidden',
    },
    '&:hover fieldset': {
      borderColor: t.text,
    },
    '&.Mui-focused fieldset': {
      borderColor: t.darkBlue,
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input, & .MuiSelect-select': {
    fontSize: '0.875rem',
    color: t.text,
  },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder': {
    color: t.text,
    opacity: 1,
  },
  '& .MuiInputLabel-root': {
    color: t.text,
    fontSize: '0.875rem',
    '&.Mui-focused': {
      color: t.text,
    },
    '&.MuiInputLabel-shrink': {
      fontSize: '0.875rem',
      bgcolor: t.white,
      px: 0.5,
    },
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.75rem',
  },
  '& .MuiMenuItem-root': {
    fontSize: '0.875rem',
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
  },
};

/** Dropdown menu paper — compact scrollable list + Tenant type scale */
export const harmonateSelectMenuProps = {
  PaperProps: {
    sx: {
      borderRadius: t.fieldRadius,
      maxHeight: 240,
      overflowY: 'auto',
      '& .MuiMenuItem-root': {
        fontSize: '0.875rem',
      },
    },
  },
};

export const harmonateDrawerHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 1,
  px: 2.5,
  py: 2,
  bgcolor: t.darkBlue,
  color: t.white,
  mx: -2.5,
  mt: -2.5,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
};

/** Flush header for full-bleed side drawers (Tenant Details pattern) */
export const harmonateDrawerHeaderFlushSx = {
  ...harmonateDrawerHeaderSx,
  mx: 0,
  mt: 0,
  mb: 0,
  borderRadius: 0,
};

/** Body padding / flex shell under drawer header */
export const harmonateDrawerBodySx = {
  p: 2,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  color: t.text,
};

/** Accordion section title — Tenant Details */
export const harmonateDrawerAccordionTitleSx = {
  flex: 1,
  fontWeight: 600,
  color: t.text,
};

/**
 * Drawer accordion stack:
 * - All closed → one continuous card (flush, shared border, divider only)
 * - Any open  → separate rounded cards with a gap between them
 */
export const harmonateDrawerAccordionGroupSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  mb: 1.5,
  border: `1px solid ${t.line}`,
  borderRadius: '8px',
  overflow: 'hidden',
  bgcolor: t.white,
  boxShadow: '0 1px 3px rgba(1, 48, 98, 0.08)',

  // Collapsed rows: flush inside the shared card
  '& > .MuiAccordion-root': {
    m: 0,
    borderRadius: '0 !important',
    border: 'none',
    borderBottom: `1px solid ${t.line}`,
    boxShadow: 'none',
    bgcolor: t.white,
    '&:before': { display: 'none' },
    '&:last-child': {
      borderBottom: 'none',
    },
  },

  // Any row expanded: split into individual cards
  '&:has(> .MuiAccordion-root.Mui-expanded)': {
    border: 'none',
    borderRadius: 0,
    overflow: 'visible',
    boxShadow: 'none',
    bgcolor: 'transparent',
    gap: 1.5,
    '& > .MuiAccordion-root': {
      border: `1px solid ${t.line}`,
      borderRadius: '8px !important',
      borderBottom: `1px solid ${t.line}`,
      boxShadow: '0 1px 3px rgba(1, 48, 98, 0.08)',
      overflow: 'hidden',
    },
  },
};

/**
 * Accordion root inside a drawer group — visuals owned by AccordionGroup.
 */
export const harmonateDrawerAccordionSx = {
  bgcolor: t.white,
  boxShadow: 'none',
  '&:before': { display: 'none' },
};

export const harmonateDrawerAccordionSummarySx = {
  minHeight: 48,
  px: 2,
  '&.Mui-expanded': {
    minHeight: 48,
  },
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    my: 1,
    gap: 0.5,
    '&.Mui-expanded': {
      my: 1,
    },
  },
};

/** Accordion body — flush content (grids sit edge-to-edge like Users drawer) */
export const harmonateDrawerAccordionDetailsSx = {
  p: 0,
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Page-header search on fadeGradient — same outlined white-on-gradient look
 * as Workforce Operations Business Unit / Audit Logs toolbar fields.
 */
export const harmonatePageSearchFieldSx = {
  minWidth: 250,
  ...harmonateToolbarFieldSx,
  '& .MuiInputBase-input::placeholder, & input::placeholder': {
    color: t.white,
    opacity: 1,
  },
};

export const harmonatePageSearchIconSx = {
  color: t.white,
  fontSize: 20,
};

export const harmonateDrawerAddIconSx = {
  ml: 1,
  color: t.text,
};

export const harmonateDrawerEditIconSx = {
  color: t.text,
};

export const harmonateDrawerDeleteIconSx = {
  color: t.red,
};

/** Left navigation sidebar — Figma drawer / nav tokens */
export const harmonateSidebarPaperSx = {
  bgcolor: t.drawerBg,
  borderRight: `1px solid ${t.line}`,
};

export const harmonateSidebarLogoAreaSx = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

export const harmonateSidebarLogoTextSx = {
  fontWeight: 700,
  fontSize: '1rem',
  color: t.text,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
};

export const getHarmonateSidebarItemSx = (isExpanded) => ({
  minHeight: 44,
  justifyContent: isExpanded ? 'initial' : 'center',
  px: 2.5,
  borderRadius: isExpanded ? '0 20px 20px 0' : '50%',
  mx: isExpanded ? 0 : 1,
  my: 0.5,
  color: t.text,
  '& .MuiListItemIcon-root': {
    color: t.text,
    minWidth: 0,
  },
  '&:hover': {
    bgcolor: t.secondaryTab,
    color: t.text,
    '& .MuiListItemIcon-root': {
      color: t.text,
    },
  },
  '&.Mui-selected': {
    bgcolor: t.blue,
    color: t.white,
    '& .MuiListItemIcon-root': {
      color: t.white,
    },
    '&:hover': {
      bgcolor: t.blue,
      opacity: 0.92,
      color: t.white,
      '& .MuiListItemIcon-root': {
        color: t.white,
      },
    },
  },
});

export const getHarmonateSidebarSubItemSx = () => ({
  minHeight: 40,
  justifyContent: 'initial',
  px: 2.5,
  pl: 4,
  borderRadius: '0 20px 20px 0',
  mx: 0,
  my: 0.25,
  color: t.text,
  '& .MuiListItemIcon-root': {
    color: t.text,
    minWidth: 0,
  },
  '&:hover': {
    bgcolor: t.secondaryTab,
    color: t.text,
    '& .MuiListItemIcon-root': {
      color: t.text,
    },
  },
  '&.Mui-selected': {
    bgcolor: t.secondaryTab,
    color: t.text,
    fontWeight: 600,
    '& .MuiListItemIcon-root': {
      color: t.text,
    },
    '&:hover': {
      bgcolor: t.secondaryTab,
      color: t.text,
      '& .MuiListItemIcon-root': {
        color: t.text,
      },
    },
  },
});

export const getHarmonateSidebarDisabledItemSx = (isExpanded) => ({
  ...getHarmonateSidebarItemSx(isExpanded),
  opacity: 0.45,
  cursor: 'not-allowed',
  color: t.fadedText,
  '& .MuiListItemIcon-root': {
    color: t.fadedText,
  },
  '&:hover': {
    bgcolor: 'transparent',
    color: t.fadedText,
    '& .MuiListItemIcon-root': {
      color: t.fadedText,
    },
  },
  '&.Mui-selected': {
    bgcolor: 'transparent',
    color: t.fadedText,
    '& .MuiListItemIcon-root': {
      color: t.fadedText,
    },
  },
});
